import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { History, PhoneCall } from 'lucide-react';
import { get, post } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { dateOnly, dateTime, money } from '@/lib/utils';
import { minorFromInput } from '@/components/MoneyInput';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { RecordState } from '@/components/ui/record-state';
import { Alert, DatePicker, Field, Input, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import type { Admission } from './admission/types';

interface Note {
  id: string;
  note: string;
  promisedDate: string | null;
  promisedAmountMinor: number | null;
  createdAt: string;
  author: string | null;
}

const DAY_MS = 86_400_000;

function daysSince(iso: string | null): number {
  if (!iso) return 0;
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / DAY_MS));
}

export function DuesFollowUpPage() {
  const { admissionId = '' } = useParams<{ admissionId: string }>();
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo'), '/ipd/dues');

  const admission = useQuery({
    queryKey: ['admission', admissionId],
    queryFn: () => get<Admission>(`/ipd/admissions/${admissionId}`),
    enabled: Boolean(admissionId),
  });

  if (!admission.data) {
    return (
      <FormScreen title={tr('ipd', 'follow_up')} icon={PhoneCall} backTo={returnTo}>
        <RecordState error={admission.error} missing={!admission.isLoading} label={tr('ipd', 'loading_the_admission')} />
      </FormScreen>
    );
  }

  return <FollowUpForm key={admission.data.id} admission={admission.data} returnTo={returnTo} />;
}

function FollowUpForm({ admission, returnTo }: { admission: Admission; returnTo: string }) {
  const { can } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const [promisedDate, setPromisedDate] = useState('');
  const [promisedAmount, setPromisedAmount] = useState('');
  const [error, setError] = useState<string | null>(null);

  const notes = useQuery({ queryKey: ['dues-notes', admission.id], queryFn: () => get<Note[]>(`/ipd/admissions/${admission.id}/dues-notes`) });

  const add = useMutation({
    mutationFn: () =>
      post(`/ipd/admissions/${admission.id}/dues-notes`, {
        note,
        promisedDate: promisedDate || null,
        promisedAmountMinor: promisedAmount.trim() ? minorFromInput(promisedAmount) : null,
      }),
    onSuccess: () => {
      toast.success(tr('ipd', 'follow_up_recorded'));
      void queryClient.invalidateQueries({ queryKey: ['dues-notes', admission.id] });
      void queryClient.invalidateQueries({ queryKey: ['dues'] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const dueMinor = admission.account.balanceMinor < 0 ? Math.abs(admission.account.balanceMinor) : 0;
  const advanceHeldMinor = admission.status === 'absconded' ? admission.account.depositedMinor : 0;
  const manage = can('dues.manage');
  const ready = note.trim().length >= 3;
  const dirty = !add.isSuccess && (note !== '' || promisedDate !== '' || promisedAmount !== '');

  return (
    <FormScreen
      title={dueMinor > 0 ? tr('ipd', 'x_owes_x', { fullName: admission.patient.fullName, dueMinor: money(dueMinor) }) : admission.patient.fullName}
      description={tr('ipd', 'x_left_x_x_days_ago', { number: admission.number, dischargedAt: dateOnly(admission.dischargedAt), ageDays: daysSince(admission.dischargedAt) })}
      eyebrow={tr('ipd', 'dues_register')}
      icon={PhoneCall}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!manage || !ready) return;
        setError(null);
        add.mutate();
      }}
    >
      {advanceHeldMinor > 0 && (
        <Alert variant="warning">
          {tr('ipd', 'the_patient_left_without_notice_with')} {money(advanceHeldMinor)} {tr('ipd', 'of_advance_unused_it_has_not_been_refunded_it')}
        </Alert>
      )}

      <FormSection title={tr('ipd', 'notes')} icon={History} columns={1}>
        {notes.isLoading && <RecordState label={tr('ipd', 'loading')} />}
        <ul className="max-h-72 space-y-2 overflow-y-auto">
          {notes.data?.length === 0 && <li className="text-sm text-muted-foreground">{tr('ipd', 'nobody_has_followed_up_on_this_yet')}</li>}
          {notes.data?.map((entry) => (
            <li key={entry.id} className="rounded-md border p-2 text-sm">
              <p className="whitespace-pre-wrap">{entry.note}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                {entry.author ?? tr('ipd', 'someone')} · {dateTime(entry.createdAt)}
                {entry.promisedDate ? ` · promised ${dateOnly(entry.promisedDate)}` : ''}
                {entry.promisedAmountMinor ? ` · ${money(entry.promisedAmountMinor)}` : ''}
              </p>
            </li>
          ))}
        </ul>
      </FormSection>

      {manage && (
        <FormSection title={tr('ipd', 'follow_up')} icon={PhoneCall}>
          <Field label={tr('ipd', 'what_happened')} required className="sm:col-span-2">
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder={tr('ipd', 'called_the_brother_he_will_pay_half_on_friday')} />
          </Field>
          <Field label={tr('ipd', 'promised_date')}>
            <DatePicker value={promisedDate} onChange={setPromisedDate} />
          </Field>
          <Field label={tr('ipd', 'promised_amount')}>
            <Input type="text" dir="ltr" inputMode="decimal" value={promisedAmount} onChange={(e) => setPromisedAmount(e.target.value)} />
          </Field>
        </FormSection>
      )}

      {error && <Alert variant="destructive">{error}</Alert>}

      {manage && <FormActions submitLabel={tr('ipd', 'record_follow_up')} loading={add.isPending} incomplete={!ready} cancelTo={returnTo} />}
    </FormScreen>
  );
}
